import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles, Box } from '@material-ui/core';
import { useFormikContext } from 'formik';
import { showToast } from '@klpkonsern/ui';

import type { FormField } from '@klp/formgenerator/interfaces';
import type { FormValuesType } from '@klp/formgenerator/components/FormContainer';
import { ActionButton } from '@klp/design';
import { RisikovurderingUrl } from '../config';
import { useLazyGetForetakVurderingQuery } from '../services/rtkApi';
import type { LEVERANDOR_REGIONS } from '../types';
import { mapRegionToCountryCode } from '../utils';

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    button: {
        textTransform: 'none',
    },
}));

type RisikovurderingSelectorProps = {
    field: FormField;
    isReadOnly: boolean;
    orgnummer: string;
    region?: LEVERANDOR_REGIONS;
    superofficeId: string;
};

export const RisikovurderingSelector: React.FC<
    RisikovurderingSelectorProps
> = ({ orgnummer, region, superofficeId, field, isReadOnly }) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const { setFieldValue } = useFormikContext<FormValuesType>();
    const [fetchForetakVurdering, { isLoading }] =
        useLazyGetForetakVurderingQuery();

    const countryCode = region
        ? mapRegionToCountryCode(region).split('-')[1]
        : 'NO';

    const handleOpenRisikovurdering = useCallback(async () => {
        const baseUrl = RisikovurderingUrl();
        const params = new URLSearchParams();

        if (orgnummer) {
            params.append('orgnummer', orgnummer);
        }
        if (countryCode) {
            params.append('land', countryCode);
        }
        if (superofficeId) {
            params.append('contactId', superofficeId);
        }

        params.append('kredittsjekk', 'true');

        const url =
            params.toString() === ''
                ? baseUrl
                : `${baseUrl}?${params.toString()}`;

        window.open(url, '_blank');
    }, [orgnummer, countryCode, superofficeId]);

    const handleFetchAndPopulateFields = useCallback(async () => {
        if (!orgnummer || isReadOnly) {
            return;
        }

        try {
            const response = await fetchForetakVurdering({
                orgNumber: orgnummer,
                countryCode,
                superofficeId,
            }).unwrap();

            const foretakData = response.data;
            const foretakNotFound = response.status === 'IkkeFunnetIDNB';

            if (foretakNotFound) {
                showToast({
                    body: t('leverandor.risikovurdering.notFoundInDNB'),
                    type: 'warning',
                });
                return;
            }

            if (foretakData) {
                foretakData.rating &&
                    setFieldValue('kredittrating', foretakData.rating);

                foretakData.betalingsevne &&
                    setFieldValue('betalingsevne', foretakData.betalingsevne);

                foretakData.betalingsanmerkning &&
                    setFieldValue(
                        'betalingsanmerkninger',
                        foretakData.betalingsanmerkning,
                    );
                setFieldValue('soliditet', foretakData.soliditet);

                foretakData.mvaRegistrert &&
                    setFieldValue(
                        'mvaregistrert',
                        foretakData.mvaRegistrert === 'Ja',
                    );

                setFieldValue('harRevisor', Boolean(foretakData.revisorInfo));
                setFieldValue(
                    'anmerkningRevisor',
                    Array.isArray(foretakData.revisorsKommentarerTilRegnskap) &&
                        foretakData.revisorsKommentarerTilRegnskap.length > 0,
                );

                foretakData.erAarsregnskapGodkjentInnenFristen !== null &&
                    setFieldValue(
                        'regnskapSiste3aar',
                        Boolean(foretakData.erAarsregnskapGodkjentInnenFristen),
                    );
                showToast({
                    body: t('leverandor.risikovurdering.fieldsUpdated'),
                    type: 'success',
                });
            }
        } catch (error) {
            showToast({
                body: t('leverandor.risikovurdering.fetchError'),
                type: 'error',
            });
        }
    }, [
        orgnummer,
        countryCode,
        superofficeId,
        fetchForetakVurdering,
        setFieldValue,
        isReadOnly,
    ]);

    return (
        <Box className={classes.container}>
            <ActionButton
                actionType="external"
                variant="contained"
                color="primary"
                id={`${field.id}-open`}
                translation="leverandor.risikovurdering.openButton"
                onClick={handleOpenRisikovurdering}
                disabled={isLoading}
                className={classes.button}
                isLoading={isLoading}
            />
            <ActionButton
                actionType="save"
                variant="contained"
                color="secondary"
                id={`${field.id}-update`}
                translation="leverandor.risikovurdering.updateButton"
                onClick={handleFetchAndPopulateFields}
                disabled={isLoading || isReadOnly}
                className={classes.button}
                isLoading={isLoading}
            />
        </Box>
    );
};
